/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { subscribe, unsubscribe } from "utils/keyboard";
import * as codes from "dc-preview-utils/src/Constants/key-codes";
import * as stores from "stores";
import { discovery, providers } from "dc-core";
import { getCurrentPageZoomLevel, isMaxZoomReached, isMinZoomReached, getPageViewModifier } from "selectors/zoom";
import { roundOffTo2Decimal } from "utils/MathUtils";
import { ENTER, ARROW_UP, ARROW_DOWN } from "constants/keyNames";
import {
  getTotalPages,
  isSinglePageMode,
  isCoverPageMode,
  getPageHandle,
  isFullScreenOrFitOnePageMode,
  isViewWithScrolling,
  isFullScreenMode
} from "selectors/documentSelectors";
import {
  pageViewModifiers,
  docViewModes,
  VERB_FULL_SCREEN,
  featureLifecyleHooks,
  ZOOM_TRIGGER_TYPE
} from "constants/enums";
import { isHorizontallyScrollable, isVeritcallyScrollable } from "selectors/pagedViewSelectors";
import { focusNextElement, isDescendant, isDialog, isTextArea, isBody } from "utils/DomUtils";
import { isControlORCommandPressed } from "dc-preview-utils/src/Utils/EventHandlingUtils";
import {
  trackRotateView,
  trackRotateViewInFillAndSign,
  trackTwoColumnViewMode,
  trackCoverPageViewMode,
  trackEntryInFitOneFullPageMode
} from "analytics/events";
import { getShouldUseModernViewer } from "../selectors/appSelectors";
import { featureLifecycleHookController } from "publicApis/controller/featureLifecycleHookController";
import { getErrorBoundaryLogger } from "../logging/logToServer";
import { shouldFetchAccContent } from "selectors/accessibilitySelectors";
import { getPagesViewXPos, getPagesViewTotalHeight } from "selectors/pagedViewState";
import { getPagesViewElementHandle } from "selectors/pagedViewState";
import { shouldEnableFitOneFullPageMode } from "selectors/pageByPageSelectors";
import { isMac } from "dc-preview-utils/src/Utils/DeviceUtils";
import { moveCaretToFirstVisibleCoordinate } from "utils/CaretModeUtils";
import { getCaretMode } from "selectors/caretModeSelectors";
import { getPageNumberInputHandle } from "selectors/pagedViewState";
import { exitFullScreenView } from "utils/PageByPageViewUtils";
import { fullScreenExitReason } from "constants/enums";
import { KEYBOARD_SHORTCUTS } from "constants/constants";
import { isSafari } from "dc-preview-utils/src/Utils/UserAgentUtils";
import { focusMainElement } from "../utils/DomUtils";

let hudHandle = null;

export const init = elementHandle => {
  stores.hudStore.setHudHandle(elementHandle);
  hudHandle = elementHandle;
  subscribe(handleKeyboardNavigation);

  providers["keyboard-shortcuts-provider"]().then(shortcutManager => {
    const modKey = isMac() ? "Meta" : "Ctrl";
    shortcutManager.addShortcuts([
      {
        id: KEYBOARD_SHORTCUTS.ROTATE,
        shortcutExprs: [`Shift+${modKey}+NumpadAdd`, `Shift+${modKey}+Equal`],
        handler: () => {
          rotatePageHandler(90);
          return { shouldPreventDefault: true };
        }
      },
      {
        id: KEYBOARD_SHORTCUTS.ROTATE_ANTI_CLOCKWISE,
        shortcutExprs: [`Shift+${modKey}+NumpadSubtract`, `Shift+${modKey}+Minus`],
        handler: () => {
          rotatePageHandler(270);
          return { shouldPreventDefault: true };
        }
      },
      {
        id: KEYBOARD_SHORTCUTS.GOTO_PAGE,
        shortcutExprs: [`${modKey}+Alt+G`],
        handler: () => {
          const pageNumberInputHandle = getPageNumberInputHandle(stores);
          if (pageNumberInputHandle) {
            pageNumberInputHandle.focus();
            pageNumberInputHandle.select();
          }
          return { shouldPreventDefault: true };
        }
      },
      {
        id: KEYBOARD_SHORTCUTS.FULL_SCREEN,
        shortcutExprs: [isSafari() ? `Shift+${modKey}+M` : `Shift+${modKey}+L`],
        handler: () => {
          if (isFullScreenMode(stores)) {
            exitFullScreenView(stores, fullScreenExitReason.KEYBOARD_SHORTCUT);
          } else {
            fullScreenModeHandler();
          }
          return { shouldPreventDefault: true };
        }
      }
    ]);
  });
};

export const cleanup = () => {
  unsubscribe(handleKeyboardNavigation);
  hudHandle = null;
  stores.hudStore.setHudHandle(hudHandle);
  providers["keyboard-shortcuts-provider"]().then(shortcutManager => {
    if (shortcutManager) {
      shortcutManager.removeShortcuts([
        KEYBOARD_SHORTCUTS.ROTATE,
        KEYBOARD_SHORTCUTS.ROTATE_ANTI_CLOCKWISE,
        KEYBOARD_SHORTCUTS.GOTO_PAGE,
        KEYBOARD_SHORTCUTS.FULL_SCREEN
      ]);
    }
  });
};

export const handleKeyboardNavigation = event => {
  if (isDialog(document.activeElement) || isTextArea(document.activeElement)) return false;
  const targetElement = event.target;
  const arrowKeyEvents = [codes.ARROW_UP, codes.ARROW_DOWN, codes.ARROW_LEFT, codes.ARROW_RIGHT];
  if (
    arrowKeyEvents.includes(event.keyCode) &&
    !isBody(targetElement) &&
    !getPagesViewElementHandle(stores).contains(targetElement)
  )
    return false; // prevent page naviagtion when focus is on menus, flyout, lists etc
  switch (event.keyCode) {
    case codes.ARROW_UP:
      // Go to prev page if in fit-page mode or single page mode is ON. Move directly to first page if Cmd+Up is pressed in single page mode
      if (isSinglePageMode(stores) && isControlORCommandPressed(event)) {
        stores.pagedViewState.goToPage(1);
        return true;
      } else if (
        isSinglePageMode(stores) ||
        getPageViewModifier(stores) === pageViewModifiers.FIT_PAGE ||
        (isFullScreenOrFitOnePageMode(stores) && !isVeritcallyScrollable(stores))
      ) {
        stores.pagedViewState.goToPrevPage();
        return true;
      } else if (document.activeElement === document.body) {
        focusMainElement();
      }
      return false;
    case codes.ARROW_DOWN:
      // Go to next page if in fit-page mode or single page mode is ON. Move directly to first page if Cmd+Down is pressed in single page mode
      if (isSinglePageMode(stores) && isControlORCommandPressed(event)) {
        stores.pagedViewState.goToPage(getTotalPages(stores));
        return true;
      } else if (
        isSinglePageMode(stores) ||
        getPageViewModifier(stores) === pageViewModifiers.FIT_PAGE ||
        (isFullScreenOrFitOnePageMode(stores) && !isVeritcallyScrollable(stores))
      ) {
        stores.pagedViewState.goToNextPage();
        return true;
      } else if (document.activeElement === document.body) {
        focusMainElement();
      }
      return false;
    case codes.ARROW_LEFT:
      // When focus is in HUD group, arow left right focuss next or previous elementHandle
      // If first elemnt and left arrow is clicked, don't change focus
      if (focusNextElement(hudHandle, false) || isDescendant(hudHandle, document.activeElement)) {
        return true;
      }
      // Go to prev page if not horizontally scrollable
      if (!isHorizontallyScrollable(stores)) {
        stores.pagedViewState.goToPrevPage();
        return true;
      }

      return false;
    case codes.ARROW_RIGHT:
      // When focus is in HUD group, arow left right focuss next or previous elementHandle
      // If last elemnt and right arrow is clicked, don't change focus
      if (focusNextElement(hudHandle, true) || isDescendant(hudHandle, document.activeElement)) {
        return true;
      }
      // Go to next page if not horizontally scrollable
      if (!isHorizontallyScrollable(stores)) {
        stores.pagedViewState.goToNextPage();
        return true;
      }
      return false;
    case codes.PAGE_UP:
      if (
        isSinglePageMode(stores) ||
        getPageViewModifier(stores) === pageViewModifiers.FIT_PAGE ||
        isFullScreenOrFitOnePageMode(stores)
      ) {
        stores.pagedViewState.goToPrevPage();
        return true;
      }
      return false;
    case codes.PAGE_DOWN:
      if (
        isSinglePageMode(stores) ||
        getPageViewModifier(stores) === pageViewModifiers.FIT_PAGE ||
        isFullScreenOrFitOnePageMode(stores)
      ) {
        stores.pagedViewState.goToNextPage();
        return true;
      }
      return false;
    case codes.HOME:
      if (isFullScreenOrFitOnePageMode(stores)) {
        stores.pagedViewState.goToPage(1);
        return true;
      }
      // Go to first page, preserving the horizontal scroll
      stores.pagedViewState.goToLocation(0, getPagesViewXPos(stores));
      return true;
    case codes.END:
      if (isFullScreenOrFitOnePageMode(stores)) {
        stores.pagedViewState.goToPage(getTotalPages(stores));
        return true;
      }
      // Go to the end, preserving the horizontal scroll
      stores.pagedViewState.scrollPagesView(getPagesViewXPos(stores), getPagesViewTotalHeight(stores));
      return true;
    case codes.SPACE:
      if (isFullScreenOrFitOnePageMode(stores)) {
        event.shiftKey ? stores.pagedViewState.goToPrevPage() : stores.pagedViewState.goToNextPage();
        return true;
      }
      return false;

    default:
      return false;
  }
};

export const zoomInHandler = () => {
  // Unload progressive page on zoom in after drawing, so that existing zoom flow can take place
  stores.documentStore.unloadAllProgressivePages();
  stores.zoom.zoomIn(ZOOM_TRIGGER_TYPE.NAV_BAR);
  let currZoomLevel = roundOffTo2Decimal(getCurrentPageZoomLevel(stores) * 100);
  if (isMaxZoomReached(stores)) {
    stores.previewStore.setAriaLiveMessage({ id: "arialive.maxZoom", values: { zoomLevel: currZoomLevel } });
  } else {
    stores.previewStore.setAriaLiveMessage({ id: "arialive.zoomIn", values: { zoomLevel: currZoomLevel } });
  }
};

export const zoomOutHandler = () => {
  // Unload progressive page on zoom out after drawing, so that existing zoom flow can take place
  stores.documentStore.unloadAllProgressivePages();
  stores.zoom.zoomOut(ZOOM_TRIGGER_TYPE.NAV_BAR);
  let currZoomLevel = roundOffTo2Decimal(getCurrentPageZoomLevel(stores) * 100);
  if (isMinZoomReached(stores)) {
    stores.previewStore.setAriaLiveMessage({
      id: "arialive.minZoom",
      values: { zoomLevel: currZoomLevel }
    });
  } else {
    stores.previewStore.setAriaLiveMessage({
      id: "arialive.zoomOut",
      values: { zoomLevel: currZoomLevel }
    });
  }
};

export const handleKeyPressOnPageNumberUI = (event, value) => {
  let keyPressHandled = false;
  if (event.key === ENTER) {
    let pageNum = parseInt(value, 10);
    stores.pagedViewState.goToPage(pageNum);
    if (getCaretMode(stores)) setTimeout(() => moveCaretToFirstVisibleCoordinate(true, false), 500);
    if (shouldFetchAccContent(stores)) {
      const pageIndex = pageNum - 1;
      const pageHandle = getPageHandle(stores, pageIndex);
      if (pageHandle) {
        pageHandle.focus();
      } else {
        stores.accessibilityStore.setPageIndexToFocus(pageIndex);
      }
    }
    event.preventDefault();
    keyPressHandled = true;
  }
  return keyPressHandled;
};

export const handleKeyDownOnPageNumberUI = event => {
  let keyPressHandled = false;
  if (event.key === ARROW_UP) {
    stores.pagedViewState.goToPrevPage();
    event.preventDefault();
    keyPressHandled = true;
  } else if (event.key === ARROW_DOWN) {
    stores.pagedViewState.goToNextPage();
    event.preventDefault();
    keyPressHandled = true;
  }
  return keyPressHandled;
};

export const fitPageHandler = () => {
  stores.zoom.setPageViewModifier({
    newPageViewModifier: pageViewModifiers.FIT_PAGE
  });
};

export const fitOneFullPageHandler = () => {
  if (shouldEnableFitOneFullPageMode(stores)) {
    stores.documentStore.toggleViewWithScrolling(false);
    stores.documentStore.setViewMode(docViewModes.FIT_ONE_FULL_PAGE);
    trackEntryInFitOneFullPageMode();
  }
};

export const twoColumnHandler = (setTwoColumnMode = true) => {
  trackTwoColumnViewMode(setTwoColumnMode);
  setTwoColumnMode
    ? stores.documentStore.setViewMode(docViewModes.TWO_COLUMN)
    : stores.documentStore.setViewMode(docViewModes.CLASSIC_VIEW);
};

export const coverModeHandler = () => {
  trackCoverPageViewMode(!isCoverPageMode(stores));
  stores.documentStore.toggleCoverMode();
};

export const viewWithScrollingToggleHandler = () => {
  stores.documentStore.toggleViewWithScrolling();
  if (!isViewWithScrolling(stores)) {
    fitOneFullPageHandler();
  } else {
    stores.documentStore.setViewMode(docViewModes.CLASSIC_VIEW);
    fitPageHandler();
  }
};

export const rotatePageHandler = async (rotation = 90) => {
  try {
    await featureLifecycleHookController.trigger(featureLifecyleHooks.BEFORE_ROTATE_VIEW);
    trackRotateView(rotation);
    stores.documentStore.setViewRotation(rotation);
  } catch (error) {
    trackRotateViewInFillAndSign();
    const logger = getErrorBoundaryLogger();
    logger.error(`Error in Rotate View on click`);
  }
};

export const fitWidthHandler = () => {
  let isCommentsPanelVisible = stores.zoom.getIsCommentsPanelVisible();
  if (isCommentsPanelVisible && !getShouldUseModernViewer(stores)) {
    stores.zoom.setPageViewModifier({
      newPageViewModifier: pageViewModifiers.FIXED_WIDTH,
      fixedMargins: { left: 0, right: 0 }
    });
  } else {
    stores.zoom.setPageViewModifier({
      newPageViewModifier: pageViewModifiers.FIT_WIDTH
    });
  }
};

export const fixWidthHandler = (args = {}) => {
  stores.zoom.setPageViewModifier({
    newPageViewModifier: pageViewModifiers.FIXED_WIDTH,
    fixedMargins: { left: args.left ? args.left : 0, right: args.right ? args.right : 0 }
  });
};

export const fullScreenModeHandler = () => {
  stores.pageByPageStore.setActiveMegaVerbBeforeFullScreen();
  featureLifecycleHookController
    .trigger(featureLifecyleHooks.BEFORE_NAVIGATION_BAR_TOOL_ACTIVATED, VERB_FULL_SCREEN)
    .then(() =>
      featureLifecycleHookController.trigger(
        featureLifecyleHooks.SAVE_BEFORE_NAVIGATION_BAR_TOOL_ACTIVATED,
        VERB_FULL_SCREEN
      )
    )
    .then(() => {
      discovery.callBootstrapMethod(VERB_FULL_SCREEN, "initializeAction", {}).then(() => {
        discovery.callBootstrapMethod(VERB_FULL_SCREEN, "executeAction", {});
      });
    });
};
